import { useReadContracts } from 'wagmi';
import { type Address } from 'viem';
import { polygon, bsc, mainnet } from 'wagmi/chains';
import { TOKEN_ABI, FAR_TOKEN_ADDRESSES } from '../constants';

interface UseMultiChainBalancesParams {
  address: Address | undefined;
}

/**
 * Hook for fetching FAR token balances across all chains
 * Shows balances from Polygon, BSC, and Ethereum regardless of current network
 */
export const useMultiChainBalances = ({ address }: UseMultiChainBalancesParams) => {
  // Fetch balances from all 3 chains in parallel
  const { data, isLoading, refetch } = useReadContracts({
    contracts: [
      // Polygon balance
      {
        address: FAR_TOKEN_ADDRESSES[polygon.id],
        abi: TOKEN_ABI,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
        chainId: polygon.id,
      },
      // BSC balance
      {
        address: FAR_TOKEN_ADDRESSES[bsc.id],
        abi: TOKEN_ABI,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
        chainId: bsc.id,
      },
      // Ethereum balance
      {
        address: FAR_TOKEN_ADDRESSES[mainnet.id],
        abi: TOKEN_ABI,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
        chainId: mainnet.id,
      },
      // Token decimals (same on all chains)
      {
        address: FAR_TOKEN_ADDRESSES[polygon.id],
        abi: TOKEN_ABI,
        functionName: 'decimals',
        chainId: polygon.id,
      },
    ],
    query: {
      enabled: !!address,
    },
  });

  return {
    polygonBalance: data?.[0]?.result as bigint | undefined,
    bscBalance: data?.[1]?.result as bigint | undefined,
    ethereumBalance: data?.[2]?.result as bigint | undefined,
    decimals: (data?.[3]?.result as number) ?? 18,
    isLoading,
    refetch,
  };
};
